import React, { type FC, type KeyboardEvent, memo, type ReactNode, useCallback, useMemo } from "react";
import { Controller, type ControllerProps, type FieldPath, useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { FieldError, Input, Label, Radio, RadioGroup, TextArea, TextField, Tooltip } from "@heroui/react";
import { CircleInfo } from "@gravity-ui/icons";
import { GQL } from "@binale-tech/shared";

import {
    type BewirtungFormValues,
    splitParticipants,
    useBewirtungRules,
} from "@dms/components/Bewirtung/bewirtungValidation";
import { AmountInput, type CurrencyValue } from "@ui-components/Inputs/AmountInput/AmountInput";
import { DateInput } from "@ui-components/Inputs/DateInput";

type Render<N extends FieldPath<BewirtungFormValues>> = ControllerProps<BewirtungFormValues, N>["render"];

const KINDS: { kind: GQL.IDmsBewirtungKind; labelId: string }[] = [
    { kind: GQL.IDmsBewirtungKind.Business, labelId: "app.dms.bewirtung.kind.business" },
    { kind: GQL.IDmsBewirtungKind.EmployeesOnly, labelId: "app.dms.bewirtung.kind.employees" },
];

const LABEL_CLASS = "font-semibold";

const Hint: FC<{ messageId: string; testId: string }> = ({ messageId, testId }) => (
    <Tooltip delay={0} closeDelay={0}>
        <Tooltip.Trigger>
            <CircleInfo className="size-4 text-muted" data-testid={testId} />
        </Tooltip.Trigger>
        <Tooltip.Content className="max-w-sm">
            <FormattedMessage id={messageId} />
        </Tooltip.Content>
    </Tooltip>
);

const DATE_LABEL = <FormattedMessage id="app.dms.bewirtung.date" />;
const AMOUNT_LABEL = (
    <span className={`${LABEL_CLASS} inline-flex items-center gap-1`}>
        <FormattedMessage id="app.dms.bewirtung.amount" />
        <Hint messageId="app.dms.bewirtung.amountHint" testId="bewirtung-amount-hint" />
    </span>
);
const TIP_LABEL = (
    <span className={LABEL_CLASS}>
        <FormattedMessage id="app.dms.bewirtung.tip" />
    </span>
);

// The DMS document form moves focus on Enter; a participant list needs Enter for the next line.
const keepEnterInTextArea = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
        event.stopPropagation();
    }
};

type BewirtungAmountFieldProps = {
    value: number | null | undefined;
    onChange: (cents: number | null) => void;
    label: ReactNode;
    isRequired: boolean;
    error?: string;
    testId: string;
};

const BewirtungAmountFieldImpl: FC<BewirtungAmountFieldProps> = ({
    value,
    onChange,
    label,
    isRequired,
    error,
    testId,
}) => {
    const handleChange = useCallback(({ amount }: CurrencyValue) => onChange(amount), [onChange]);
    return (
        <AmountInput
            value={value ?? undefined}
            onChange={handleChange}
            label={label}
            isRequired={isRequired}
            isInvalid={!!error}
            errorMessage={error}
            data-testid={testId}
        />
    );
};

const BewirtungAmountField = memo(BewirtungAmountFieldImpl);

type BewirtungParticipantsFieldProps = {
    value: string[] | undefined;
    onChange: (participants: string[]) => void;
    onBlur: () => void;
    error?: string;
};

const BewirtungParticipantsFieldImpl: FC<BewirtungParticipantsFieldProps> = ({ value, onChange, onBlur, error }) => {
    const handleChange = useCallback((text: string) => onChange(splitParticipants(text)), [onChange]);
    return (
        <TextField
            value={(value ?? []).join("\n")}
            onChange={handleChange}
            onBlur={onBlur}
            isInvalid={!!error}
            isRequired
            fullWidth
        >
            <Label className={LABEL_CLASS}>
                <FormattedMessage id="app.dms.bewirtung.participants" />
            </Label>
            <TextArea rows={3} data-testid="bewirtung-participants" onKeyDown={keepEnterInTextArea} />
            <FieldError>{error}</FieldError>
        </TextField>
    );
};

const BewirtungParticipantsField = memo(BewirtungParticipantsFieldImpl);

const BewirtungFieldsImpl: FC = () => {
    const intl = useIntl();
    const { control, trigger, getFieldState, setValue } = useFormContext<BewirtungFormValues>();
    // Same condition as the occasion rule: only a business Bewirtung needs an occasion.
    const isOccasionRequired = useWatch({ control, name: "bewirtung.kind" }) === GQL.IDmsBewirtungKind.Business;
    const rules = useBewirtungRules();

    const handleKindChange = useCallback(
        (value: string) => {
            const kind = KINDS.find((option) => option.kind === value)?.kind;
            if (!kind) {
                return;
            }
            setValue("bewirtung.kind", kind);
            if (getFieldState("bewirtung.occasion").error) {
                void trigger("bewirtung.occasion");
            }
        },
        [getFieldState, setValue, trigger],
    );

    const renderKind = useCallback<Render<"bewirtung.kind">>(
        ({ field }) => (
            <RadioGroup
                orientation="horizontal"
                value={field.value ?? null}
                onChange={handleKindChange}
                aria-label={intl.formatMessage({ id: "app.dms.bewirtung.title" })}
            >
                {KINDS.map(({ kind, labelId }) => (
                    <Radio key={kind} value={kind}>
                        <Radio.Content>
                            <Radio.Control className="border">
                                <Radio.Indicator />
                            </Radio.Control>
                            <FormattedMessage id={labelId} />
                        </Radio.Content>
                    </Radio>
                ))}
            </RadioGroup>
        ),
        [handleKindChange, intl],
    );

    const renderDate = useCallback<Render<"bewirtung.date">>(
        ({ field, fieldState }) => (
            <div className="flex flex-col gap-1">
                <DateInput
                    value={field.value || undefined}
                    onChange={field.onChange}
                    label={DATE_LABEL}
                    isRequired
                    data-testid="bewirtung-date"
                />
                {fieldState.error && <p className="text-xs text-danger">{fieldState.error.message}</p>}
            </div>
        ),
        [],
    );

    const renderText = useCallback(
        (
            labelId: string,
            testId: string,
            isRequired: boolean,
            hintId?: string,
        ): Render<"bewirtung.place" | "bewirtung.occasion" | "bewirtung.host"> =>
            ({ field, fieldState }) => (
                <TextField
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isInvalid={!!fieldState.error}
                    isRequired={isRequired}
                    fullWidth
                >
                    <div className="flex items-center gap-1">
                        {/* explicit: the wrapper div keeps the asterisk from following TextField's isRequired */}
                        <Label className={LABEL_CLASS} isRequired={isRequired}>
                            <FormattedMessage id={labelId} />
                        </Label>
                        {hintId && <Hint messageId={hintId} testId={`${testId}-hint`} />}
                    </div>
                    <Input data-testid={testId} />
                    <FieldError>{fieldState.error?.message}</FieldError>
                </TextField>
            ),
        [],
    );
    const renderPlace = useMemo(() => renderText("app.dms.bewirtung.place", "bewirtung-place", true), [renderText]);
    const renderOccasion = useMemo(
        () => renderText("app.dms.bewirtung.occasion", "bewirtung-occasion", isOccasionRequired),
        [renderText, isOccasionRequired],
    );
    const renderHost = useMemo(
        () => renderText("app.dms.bewirtung.host", "bewirtung-host", true, "app.dms.bewirtung.hostHint"),
        [renderText],
    );

    const renderParticipants = useCallback<Render<"bewirtung.participants">>(
        ({ field, fieldState }) => (
            <BewirtungParticipantsField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
            />
        ),
        [],
    );

    const renderAmount = useCallback(
        (
            label: ReactNode,
            testId: string,
            isRequired: boolean,
        ): Render<"bewirtung.amountCents" | "bewirtung.tipCents"> =>
            ({ field, fieldState }) => (
                <BewirtungAmountField
                    value={field.value}
                    onChange={field.onChange}
                    label={label}
                    isRequired={isRequired}
                    error={fieldState.error?.message}
                    testId={testId}
                />
            ),
        [],
    );
    const renderAmountCents = useMemo(() => renderAmount(AMOUNT_LABEL, "bewirtung-amount", true), [renderAmount]);
    const renderTipCents = useMemo(() => renderAmount(TIP_LABEL, "bewirtung-tip", false), [renderAmount]);

    return (
        <div className="flex flex-col gap-3" data-testid="bewirtung-fields">
            <Controller name="bewirtung.kind" control={control} render={renderKind} />
            <Controller name="bewirtung.date" control={control} rules={rules.date} render={renderDate} />
            <Controller name="bewirtung.place" control={control} rules={rules.text} render={renderPlace} />
            <Controller
                name="bewirtung.participants"
                control={control}
                rules={rules.participants}
                render={renderParticipants}
            />
            <Controller name="bewirtung.occasion" control={control} rules={rules.occasion} render={renderOccasion} />
            <div className="grid grid-cols-2 gap-3">
                <Controller
                    name="bewirtung.amountCents"
                    control={control}
                    rules={rules.amount}
                    render={renderAmountCents}
                />
                <Controller name="bewirtung.tipCents" control={control} rules={rules.tip} render={renderTipCents} />
            </div>
            <Controller name="bewirtung.host" control={control} rules={rules.text} render={renderHost} />
        </div>
    );
};

export const BewirtungFields = memo(BewirtungFieldsImpl);
